import React, { FunctionComponent } from "react";
import { Trans, useTranslation } from "react-i18next";

import { styled, hsla, Button } from "../../../seenspire-library";
import ContinueOnDesktopIcon from "../../../assets/continue_on_desktop_icon.svg";
import SeenspireLogo from "../../../assets/no_mobile_logo.svg";

const CONFIRMATION_GREEN = "hsl(145, 63%, 58%)";
/* Small phones can't hold the frame at full size; everything steps down so the
   logout stays above the fold rather than needing a scroll. */
const COMPACT = "@media (max-height: 640px), (max-width: 360px)";
const BACKGROUND = "#0a0e17";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 19999;
  display: flex;
  /* Scrolls rather than clipping when a short viewport can't hold the frame.
     The frame stretches to this box rather than being centred in it, so an
     overflowing top stays reachable. */
  overflow-y: auto;
  padding: 64px 16px;
  background: ${BACKGROUND};
  text-align: center;

  /* Short viewports need that outer air back as usable height. */
  ${COMPACT} {
    padding: 40px 16px;
  }
`;

/**
 * Stretches to fill the overlay's height — the default `align-items: stretch`
 * — and centres its content, so the frame reads as a tall panel with air above
 * and below rather than a box shrink-wrapped around the text.
 */
const Frame = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  padding: 48px 32px;
  border: 1px solid rgba(206, 212, 218, 0.3);
  border-radius: 4px;

  ${COMPACT} {
    padding: 40px 14px;
  }
`;

/* Sits on the frame's top edge, its own background masking the line behind. */
const Logo = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  /* Centred on the frame's top edge in both axes; its own background is what
     breaks the border line behind it. */
  transform: translate(-50%, -50%);
  padding: 0 20px;
  background: ${BACKGROUND};

  img {
    display: block;
  }

  ${COMPACT} {
    padding: 0 14px;

    img {
      width: 155px;
      height: 18px;
    }
  }
`;

const IconCircle = styled.div`
  width: 104px;
  height: 104px;
  margin: 0 auto 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.cta.base};
  box-shadow: 0 0 36px ${({ theme }) => hsla(theme.colors.cta.base, 0.35)};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 58px;
    height: 58px;
  }

  ${COMPACT} {
    width: 84px;
    height: 84px;
    margin-bottom: 24px;

    img {
      width: 48px;
      height: 48px;
    }
  }
`;

const Title = styled.h1`
  margin: 0;
  color: #ffffff;
  font-family: "Open Sans";
  font-size: 28px;
  font-weight: 700;
  line-height: 1.45;
  letter-spacing: -0.3px;

  ${COMPACT} {
    font-size: 24px;
  }
`;

const Description = styled.p`
  margin: 20px auto 32px;
  color: rgba(255, 255, 255, 0.5);
  font-family: "Open Sans";
  font-size: 16px;
  font-weight: 400;
  line-height: 1.7;

  strong {
    color: rgba(255, 255, 255, 0.75);
    font-weight: 600;
  }

  ${COMPACT} {
    margin: 16px auto 24px;
  }
`;

const ActionButton = styled(Button)`
  min-height: 64px;
  border-radius: 8px;
  font-family: "Open Sans";
  font-size: 15px;
  letter-spacing: 1.8px;

  ${COMPACT} {
    min-height: 56px;
  }
`;

/**
 * The confirmation takes the button's place rather than replacing the whole
 * view, so it matches its geometry exactly and nothing around it shifts.
 */
const Confirmation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 64px;
  padding: 0 16px;
  border: 1px solid ${hsla(CONFIRMATION_GREEN, 0.3)};
  border-radius: 8px;
  background: ${hsla(CONFIRMATION_GREEN, 0.12)};
  color: ${CONFIRMATION_GREEN};
  font-family: "Open Sans";
  font-size: 15px;
  font-weight: 700;

  ${COMPACT} {
    min-height: 56px;
  }
`;

const ErrorMessage = styled.p`
  margin: 0 auto 20px;
  color: ${({ theme }) => theme.colors.alert.base};
  font-family: "Open Sans";
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;
`;

const LogoutButton = styled.button`
  display: block;
  margin: 32px auto 0;
  padding: 8px;
  border: 0;
  background: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.45);
  font-family: "Open Sans";
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;

  ${COMPACT} {
    margin-top: 24px;
  }
`;

type SendStatus = "idle" | "sending" | "sent" | "error";

interface IContinueOnDesktopProps {
  status: SendStatus;
  onSendLink: () => void;
  onLogout: () => void;
}

const ContinueOnDesktop: FunctionComponent<IContinueOnDesktopProps> = ({
  status,
  onSendLink,
  onLogout,
}) => {
  const { t } = useTranslation();

  return (
    <Overlay>
      <Frame>
        <Logo>
          <img src={SeenspireLogo} alt="Seenspire" />
        </Logo>

        <IconCircle>
          <img src={ContinueOnDesktopIcon} alt="" />
        </IconCircle>

        {/* `Trans` renders the `<br/>` the copy carries, which keeps the
            headline on two balanced lines. */}
        <Title>
          <Trans i18nKey="continue_on_desktop.title" />
        </Title>

        <Description>
          <Trans i18nKey="continue_on_desktop.description" />
        </Description>

        {status === "error" && (
          <ErrorMessage role="alert">
            {t("continue_on_desktop.error")}
          </ErrorMessage>
        )}

        {status === "sent" ? (
          <Confirmation role="status">
            {t("continue_on_desktop.sent_confirmation")}
          </Confirmation>
        ) : (
          <ActionButton
            elementWidth="block"
            buttonType="cta"
            disabled={status === "sending"}
            onClick={onSendLink}
          >
            {t(
              status === "sending"
                ? "continue_on_desktop.sending"
                : "continue_on_desktop.send_link"
            )}
          </ActionButton>
        )}

        <LogoutButton type="button" onClick={onLogout}>
          {t("continue_on_desktop.logout")}
        </LogoutButton>
      </Frame>
    </Overlay>
  );
};

export { ContinueOnDesktop, IContinueOnDesktopProps, SendStatus };
export default ContinueOnDesktop;
